import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import ROUTES from '../../routes';
import api from '../../config/axiosInstance';

import { showToast } from '../../utils/toast';
import { handleErrors } from '../../utils/common';
import DetailModal from '../../Components/DetailModal';

const Reviews = () => {
    const Swal = window.Swal;

    const limit = 10;
    const [reviews, setReviews] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);

    const [showModal, setShowModal] = useState(false);
    const [modalData, setModalData] = useState(null);
    const [modalLoading, setModalLoading] = useState(false);

    useEffect(() => {
        const fetchReviews = async (page = 1) => {
            try {
                setLoading(true);
                const offset = (page - 1) * limit;
                const res = await api.get(`/reviews/list?limit=${limit}&offset=${offset}`);
                if (res.status === 200) {
                    setReviews(res.data.reviews || res.data.data || []);
                    const total = res.data.total || 0;
                    setTotalPages(Math.ceil(total / limit) || 1);
                }
            } catch (err) {
                handleErrors(err);
            } finally {
                setLoading(false);
            }
        };

        fetchReviews(currentPage);
    }, [currentPage]);

    const handleViewDetail = async (id) => {
        try {
            setModalLoading(true);
            setShowModal(true);
            const res = await api.get(`/reviews/${id}`);
            if (res.status === 200) {
                let data = res.data.review || res.data.data || res.data;

                delete data.product_id;
                delete data.user_id;

                setModalData(data);
            }
        } catch (err) {
            handleErrors(err);
            setShowModal(false);
        } finally {
            setModalLoading(false);
        }
    };

    const handleDelete = (id) => {
        Swal.fire({
            title: 'Are you sure?',
            html: '<i class="fas fa-trash-alt" style="font-size: 42px; color: #d33; margin-bottom: 10px;"></i><p>This will permanently delete the review!</p>',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const res = await api.delete(`/reviews/${id}`);
                    if (res.status === 200) {
                        showToast('success', res.data.message || 'Review deleted successfully', 'Deleted');
                        setReviews(prev => prev.filter(s => s._id !== id));
                    }
                } catch (err) {
                    handleErrors(err);
                }
            }
        });
    };

    return (
        <>
            <div className="page-titles">
                <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                        <Link to={ROUTES.ROOT}>Dashboard</Link>
                    </li>
                    <li className="breadcrumb-item active">
                        <Link to={ROUTES.REVIEW}>Reviews</Link>
                    </li>
                </ol>
            </div>

            <div className="row">
                <div className="col-lg-12">
                    <div className="card">
                        <div className="card-header">
                            <h4 className="card-title">Reviews</h4>
                        </div>
                        <div className="card-body">
                            <div className="table-responsive">
                                {loading ? (
                                    <div className="text-center my-4">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                ) : (
                                    <table className="table table-responsive-md">
                                        <thead>
                                            <tr>
                                                <th><strong>#</strong></th>
                                                <th><strong>Rating</strong></th>
                                                <th><strong>Review</strong></th>
                                                <th><strong>Product</strong></th>
                                                <th><strong>Actions</strong></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {reviews && reviews.length > 0 ? reviews.map((review, index) => (
                                                <tr key={review._id || index} style={{ cursor: 'pointer' }}>
                                                    <td><strong>{((currentPage - 1) * limit) + (index + 1)}</strong></td>
                                                    <td>{review.rating || 'N/A'}</td>
                                                    <td>{review.review || 'N/A'}</td>
                                                    <td>
                                                        {review.product?.title || review.product?.name || review.product || 'N/A'}
                                                    </td>
                                                    <td>
                                                        <div className="d-flex" onClick={(e) => e.stopPropagation()}>
                                                            <button
                                                                className="btn btn-primary shadow btn-xs sharp me-1"
                                                                onClick={() => handleViewDetail(review._id)}
                                                            >
                                                                <i className="fas fa-eye"></i>
                                                            </button>
                                                            <button
                                                                className="btn btn-danger shadow btn-xs sharp"
                                                                onClick={() => handleDelete(review._id)}
                                                            >
                                                                <i className="fas fa-trash"></i>
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )) : (
                                                <tr>
                                                    <td colSpan="5" className="text-center">No reviews found.</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>
                        <div className="card-footer">
                            <ul className="pagination pagination-gutter pagination-primary no-bg">
                                <li className={`page-item page-indicator ${currentPage === 1 ? 'disabled' : ''}`}>
                                    <a className="page-link" href="#/" onClick={(e) => { e.preventDefault(); setCurrentPage(prev => Math.max(prev - 1, 1)); }}>
                                        <i className="la la-angle-left"></i>
                                    </a>
                                </li>
                                {Array.from({ length: totalPages }, (_, i) => (
                                    <li className={`page-item ${currentPage === i + 1 ? 'active' : ''}`} key={i}>
                                        <a className="page-link" href="#/" onClick={(e) => { e.preventDefault(); setCurrentPage(i + 1); }}>{i + 1}</a>
                                    </li>
                                ))}
                                <li className={`page-item page-indicator ${currentPage === totalPages ? 'disabled' : ''}`}>
                                    <a className="page-link" href="#/" onClick={(e) => { e.preventDefault(); setCurrentPage(prev => Math.min(prev + 1, totalPages)); }}>
                                        <i className="la la-angle-right"></i>
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            <DetailModal
                show={showModal}
                onHide={() => setShowModal(false)}
                title="Review Detail"
                data={modalData}
                loading={modalLoading}
            />
        </>
    );
};

export default Reviews;
